import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { getFilteredPassings, getPassingsFilterOBEs, getPassingsFilterPagination, showCheckboxColumn, passingsToExport } from 'api/passings/getFilteredPassings';
import { DownloadButton } from 'components/Button';
import InfoBox from 'components/InfoBox/InfoBox';
import { PASSINGS_DOWNLOAD_URL, PASSINGS_BY_TRANSACTION_DOWNLOAD_URL } from 'constants/urls';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import dateTimeFormatter from 'utils/date/dateTimeFormatter';
import downloadBlob from 'utils/download/downloadBlob';
import isNorwegian from 'utils/language/isNorwegian';
import checkLoadable from 'utils/recoil/checkLoadable';
import useStyles from './PassingStyles';
export default function PassingsDownload() {
  const {
    t,
    i18n
  } = useTranslation();
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const {
    downloadButtons,
    column,
    pdfWarning
  } = useStyles();
  const showCheckbox = useRecoilValue(showCheckboxColumn);
  const pagination = useRecoilValue(getPassingsFilterPagination);
  const obes = useRecoilValue(getPassingsFilterOBEs);
  const passingsLoadable = useRecoilValueLoadable(getFilteredPassings);
  const {
    loading,
    contents: passings
  } = checkLoadable(passingsLoadable);
  const passingsToExportLst = useRecoilValue(passingsToExport);
  const {
    isBusinessCustomer
  } = useCurrentCustomer();
  const disabled = loading || isDownloadingCsv || isDownloadingPdf || !passings?.totalResults || showCheckbox && passingsToExportLst.length === 0;
  const handleDownloadClick = async format => {
    const url = PASSINGS_DOWNLOAD_URL;
    const filename = `${t('passings.passings')}${pagination?.dateFrom ? `_${dateTimeFormatter(pagination.dateFrom)}` : ''}${pagination?.dateFrom && pagination?.dateTo ? '-' : ''}${pagination?.dateTo ? `_${dateTimeFormatter(pagination?.dateTo)}` : ''}.${format}`;
    const data = {
      ...pagination,
      offset: 0,
      max: passings?.totalResults,
      obeNumbers: obes,
      language: isNorwegian(i18n) ? 'No' : 'Eng',
      format
    };
    await downloadBlob(url, filename, 'POST', data);
  };
  const handleDownloadByTransactionClick = async format => {
    const url = PASSINGS_BY_TRANSACTION_DOWNLOAD_URL;
    const filename = `${t('passings.passings')}${pagination?.dateFrom ? `_${dateTimeFormatter(pagination.dateFrom)}` : ''}${pagination?.dateFrom && pagination?.dateTo ? '-' : ''}${pagination?.dateTo ? `_${dateTimeFormatter(pagination?.dateTo)}` : ''}.${format}`;
    const data = {
      ...pagination,
      offset: 0,
      max: passings?.totalResults,
      obeNumbers: obes,
      language: isNorwegian(i18n) ? 'No' : 'Eng',
      format,
      transactionIds: passingsToExportLst,
      accountId: passings?.results?.[0].accountId
    };
    await downloadBlob(url, filename, 'POST', data);
  };
  const handleDownloadPdfClick = async () => {
    setIsDownloadingPdf(true);
    try {
      if (passingsToExportLst.length > 0) {
        await handleDownloadByTransactionClick('pdf');
      } else {
        await handleDownloadClick('pdf');
      }
    } finally {
      setIsDownloadingPdf(false);
    }
  };
  const handleDownloadCsvClick = async () => {
    setIsDownloadingCsv(true);
    try {
      if (passingsToExportLst.length > 0) {
        await handleDownloadByTransactionClick('csv');
      } else {
        await handleDownloadClick('csv');
      }
    } finally {
      setIsDownloadingCsv(false);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: downloadButtons
  }, /*#__PURE__*/React.createElement("div", {
    className: column
  }, /*#__PURE__*/React.createElement(DownloadButton, {
    disabled: disabled,
    loading: isDownloadingCsv,
    onClick: handleDownloadCsvClick
  }, `${t('buttons.export_passings_list')} (CSV)`)), /*#__PURE__*/React.createElement("div", {
    className: column
  }, /*#__PURE__*/React.createElement(DownloadButton, {
    disabled: disabled || isBusinessCustomer && obes.length > 2,
    loading: isDownloadingPdf,
    onClick: handleDownloadPdfClick
  }, `${t('buttons.export_passings_list')} (PDF)`))), isBusinessCustomer && obes.length > 2 && /*#__PURE__*/React.createElement(InfoBox, {
    warning: true,
    className: pdfWarning
  }, t('passings.max_2_obes')));
}